import React, { useState, useMemo } from 'react';
import { Pencil, Trash2, Star, Search, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { updateUserProfile } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import { permission } from '../../config/permission';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { AgTableCard, Input } from '../UI';

function ManageOrganisationTable({
    organisationData,
    setRenameOrganisation,
    setDeactivateCompany,
    setDefaultSuccess,
    setSelectedOrganisation
}) {
    const { userData, setUserData, loading, checkPermission } = useUserContext();
    const { content } = useContentfulContext();
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [gridApi, setGridApi] = useState(null);

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "display_name",
                    minWidth: 300,
                },
                {
                    colId: "role",
                    minWidth: 160,
                },
                {
                    colId: "default",
                    minWidth: 120,
                },
                {
                    colId: "actions",
                    minWidth: 150,
                }
            ],
        };
    }, []);

    const handleSetDefault = async (org) => {
        setIsSettingDefault(true);
        const payload = {
            user_metadata: {
                default_organization: org.name,
            },
        };
        try {
            const result = await updateUserProfile(
                userData.token,
                userData.user_id,
                payload,
                userData?.selectedOrganization?.org_token
            );
            setUserData((prev) => ({
                ...prev,
                user_metadata: result.data.user_metadata
            }));
            setDefaultSuccess(true);
            toast.success("Default organization updated successfully");
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || "Failed to set default organization";
            toast.error(errMsg);
        } finally {
            setIsSettingDefault(false);
        }
    };

    const colDefs = [
        {
            headerName: content.COMPANY_CONTENT.accounts_table_header.organisation_name,
            field: "display_name",
            suppressMovable: true,
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-stride-muted shrink-0" />
                    <span className="font-semibold text-stride-heading">
                        {data.display_name}
                    </span>
                    {data.isBlocked && (
                        <span title="You are blocked from this organization" className="stride-badge-pill bg-rose-50 text-rose-700 text-xs">
                            Blocked
                        </span>
                    )}
                </div>
            )
        },
        {
            headerName: content.COMPANY_CONTENT.accounts_table_header.organisation_role,
            field: "role",
            sortable: false,
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ value }) => (
                <span className="stride-badge-pill bg-primary-soft text-primary font-semibold text-xs capitalize">
                    {value || 'Member'}
                </span>
            )
        },
        {
            headerName: content.COMPANY_CONTENT.accounts_table_header.default,
            field: "default",
            sortable: false,
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const isDefault = data.default === 'YES';
                return isDefault ? (
                    <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
                        Default
                    </span>
                ) : (
                    <span className="text-stride-muted text-xs">—</span>
                );
            }
        },
        {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ({ data }) => {
                const isBlocked = data?.isBlocked ? true : false;
                const isDefault = data.default === 'YES';
                const canRename = checkPermission(permission.MANAGE_ORGANIZATION_OP_RENAME);
                const canDeactivate = checkPermission(permission.MANAGE_ORGANIZATION_OP_DEACTIVATE);

                if (isBlocked) return <span className="text-stride-muted text-xs">Disabled</span>;

                return (
                    <div className="stride-table-actions">
                        {canRename && (
                            <button
                                type="button"
                                title={content.COMPANY_CONTENT.cta_text.rename}
                                className="stride-table-btn stride-table-btn-edit"
                                onClick={() => {
                                    setSelectedOrganisation(data);
                                    setRenameOrganisation(true);
                                }}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}

                        <button
                            type="button"
                            title={content.COMPANY_CONTENT.cta_text.set_as_default}
                            className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                            disabled={isDefault || isSettingDefault}
                            onClick={() => handleSetDefault(data)}
                        >
                            {isDefault ? <Star fill="#f59e0b" className="w-3.5 h-3.5 text-amber-500" /> : <Star className="w-3.5 h-3.5 text-amber-500" />}
                        </button>

                        {canDeactivate && (
                            <button
                                type="button"
                                title={content.COMPANY_CONTENT.cta_text.deactivate}
                                className="stride-table-btn stride-table-btn-delete"
                                onClick={() => {
                                    setSelectedOrganisation(data);
                                    setDeactivateCompany(true);
                                }}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                );
            },
            width: 150,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
        }
    ];

    const onFilterTextBoxChanged = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (gridApi) {
            gridApi.setFilterModel({
                display_name: val ? { filterType: 'text', type: 'contains', filter: val.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            gridApi.setFilterModel({});
        }
    };

    return (
        <div className="flex flex-col space-y-6">
            {isSettingDefault && <Loader />}

            <AgTableCard
                header={
                    <div className="w-full md:w-80">
                        <Input
                            placeholder={content.COMPANY_CONTENT.search_text}
                            value={searchTerm}
                            onChange={onFilterTextBoxChanged}
                            prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                            clearable
                            onClear={handleClearSearch}
                        />
                    </div>
                }
                tableId="manage-organisation-table"
                loading={loading}
                rowData={organisationData}
                columnDefs={colDefs}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                domLayout="autoHeight"
            />
        </div>
    );
}

export default ManageOrganisationTable;
